import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getUserAttributes, type UserAttributes } from '@/services/api/user';

export const userAttributesQueryKey = (pid: UserPid) => ['user', 'attributes', pid];

const userAttributesQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: () => getUserAttributes(pid),
    queryKey: userAttributesQueryKey(pid),
  });

const useUserAttributes = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const queryResult = useQuery(userAttributesQueryOptions(pid));

  const set = useCallback(
    (userAttributes: Partial<UserAttributes>) => {
      queryClient.setQueryData(userAttributesQueryKey(pid), (previousUserAttributes: UserAttributes) => ({
        ...previousUserAttributes,
        ...userAttributes,
      }));
    },
    [pid, queryClient],
  );

  return {
    ...queryResult,
    set,
  };
};

export { useUserAttributes };
